import type { FC } from 'react';
import React from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';

import { useRouteDataRef, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState';
import { TEMPLATE_GALLERY } from '@confluence/named-routes/entry-points/routes';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback';

import { i18n } from '../globalNavigationTranslations';
import { TemplatesIconComponent } from '../GlobalNavigationIcons';

import type { GlobalItemProps } from './globalItemProps';
import { useGlobalItemVisibility } from './useGlobalItemVisibility';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks'))
			.fireTemplatesClickedAnalytics,
);

const isOnTemplatesSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl.includes(TEMPLATE_GALLERY.toUrl()); //templates adds a bunch of query components
	},
};

export const TemplatesItem: FC<GlobalItemProps> = ({ isHidden, peekingId, setPeekingId }) => {
	const routeDataRef = useRouteDataRef();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);

	const isSelected = useRouteUrl(isOnTemplatesSelector);

	const templateUrl = TEMPLATE_GALLERY.toUrl();

	const shouldHide = useGlobalItemVisibility(
		'templates',
		isSelected,
		isHidden,
		peekingId,
		setPeekingId,
	);

	if (shouldHide) {
		return null;
	}

	return (
		<LinkMenuItem
			href={templateUrl}
			elemBefore={TemplatesIconComponent}
			isSelected={isSelected}
			onClick={fireClickAnalytics}
		>
			<FormattedMessage {...i18n.templates} />
		</LinkMenuItem>
	);
};
